import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import PasswordInput from '../components/PasswordInput';
import ForgotPasswordPanel from '../components/ForgotPasswordPanel';
import { useIdleLogout } from '../hooks/useIdleLogout';

const unlockFees: Record<string, number> = {
  'Bedsitter': 150,
  '1 Bedroom': 200,
  '2 Bedroom': 300,
  '3 Bedroom': 400,
  '4 Bedroom': 500,
};

// Only this exact email is allowed into the admin panel, even if the
// Supabase login itself succeeds. Without this, any lister who signs up
// could log into /oss-sntm and get full admin access.
const ADMIN_EMAIL = 'onestopshop254kenya@gmail.com';

// Best-effort storage cleanup — turns a public Supabase Storage URL back into
// its bucket path and deletes the file. Never blocks the calling action if it fails.
const deleteStorageFile = async (bucket: string, publicUrl: string) => {
  try {
    const marker = `/${bucket}/`;
    const idx = publicUrl.indexOf(marker);
    if (idx === -1) return;
    const path = publicUrl.substring(idx + marker.length);
    if (!path) return;
    await supabase.storage.from(bucket).remove([path]);
  } catch {
    // Storage cleanup is best-effort; the listing/DB row is already gone by this point.
  }
};

const inputStyle = { width: '100%', background: 'var(--dark-3)', border: '1px solid rgba(201,168,76,0.2)', color: 'var(--text)', padding: '12px 16px', borderRadius: '4px', fontSize: '14px', outline: 'none' };
const labelStyle = { color: 'var(--text-muted)', fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase' as const, display: 'block', marginBottom: '8px' };
const sectionStyle = { background: 'rgba(201,168,76,0.03)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: '8px', padding: '24px', display: 'flex', flexDirection: 'column' as const, gap: '20px' };

type ListingRow = {
  id: string;
  title: string;
  table: 'rentals' | 'land' | 'houses_for_sale' | 'cars';
  status?: string;
  lister_id?: string;
  lister_role?: string;
  lister_whatsapp?: string;
  agreement_url?: string;
  created_at?: string;
};

type ListerRow = {
  id: string;
  email: string;
  whatsapp: string;
  role: string;
  banned: boolean;
  balance: number;
  last_paid_at?: string | null;
  created_at?: string;
};

type BlockedLister = {
  id: string;
  email: string | null;
  whatsapp: string | null;
  reason: string | null;
  blocked_at: string;
};

type ListerStat = {
  lister_id: string;
  whatsapp: string;
  totalClicks: number;
  listingCount: number;
};

type PaymentRequest = {
  id: string;
  reference_code: string;
  listing_table: string;
  listing_title: string | null;
  buyer_phone: string;
  amount: number;
  screenshot_path: string | null;
  status: string;
  payment_method: string;
  created_at: string;
};

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [mainView, setMainView] = useState<'add' | 'listings' | 'listers' | 'payments' | 'analytics'>('add');
  const [activeTab, setActiveTab] = useState<'rental' | 'land' | 'house' | 'car'>('rental');
  const [attempts, setAttempts] = useState(0);
  const [blocked, setBlocked] = useState(false);
  const [mode, setMode] = useState<'login' | 'forgot' | 'magiclink'>('login');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  useIdleLogout(isLoggedIn, '/oss-sntm');

  const [listings, setListings] = useState<ListingRow[]>([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [listers, setListers] = useState<ListerRow[]>([]);
  const [loadingListers, setLoadingListers] = useState(false);
  const [earningsInput, setEarningsInput] = useState<Record<string, string>>({});
  const [filterListerId, setFilterListerId] = useState<string | null>(null);
  const [listerStats, setListerStats] = useState<ListerStat[]>([]);
  const [blockedListers, setBlockedListers] = useState<BlockedLister[]>([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequest[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const [rentalForm, setRentalForm] = useState({ title: '', building_name: '', type: 'Bedsitter', bedrooms: 0, price_per_month: '', description: '', whatsapp: '', google_maps_link: '' });
  const [landForm, setLandForm] = useState({
    title: '', county: '', location: '', size: '', price: '', type: 'Residential', description: '',
    whatsapp: '', second_whatsapp: '', google_maps_link: '', agent_name: '',
    water_on_site: '', water_in_area: '', electricity_on_site: '', electricity_in_area: '',
    title_deed: '', document_type: '', access_road_type: '', distance_to_tarmac: '',
    has_disputes: '', beacons_confirmed: '', nearby_amenities: '', negotiable: '', payment_plan_available: '',
  });
  const [houseForm, setHouseForm] = useState({ title: '', building_name: '', location: '', price: '', bedrooms: '', bathrooms: '', type: 'Apartment', description: '', whatsapp: '', google_maps_link: '' });
  const [carForm, setCarForm] = useState({ title: '', make: '', model: '', year: '', mileage: '', price: '', type: 'Sedan', transmission: 'Automatic', location: '', description: '', whatsapp: '' });

  const [listerForm, setListerForm] = useState({ role: 'Caretaker', whatsapp: '' });
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [agreementFile, setAgreementFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && session.user.email?.toLowerCase() !== ADMIN_EMAIL) {
        // Someone else's valid login (e.g. a lister) landed on this page —
        // kick them out rather than showing the admin panel.
        supabase.auth.signOut();
        setIsLoggedIn(false);
      } else {
        setIsLoggedIn(!!session);
      }
      setCheckingSession(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session && session.user.email?.toLowerCase() !== ADMIN_EMAIL) {
        supabase.auth.signOut();
        setIsLoggedIn(false);
      } else {
        setIsLoggedIn(!!session);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchAllListings();
      fetchListers();
      fetchPaymentRequests();
      fetchBlockedListers();
    }
  }, [isLoggedIn]);

  const fetchAllListings = async () => {
    setLoadingListings(true);
    const tablesWithAgreements: ListingRow['table'][] = ['rentals', 'land', 'houses_for_sale'];
    let all: ListingRow[] = [];
    for (const table of tablesWithAgreements) {
      const { data, error: fetchError } = await supabase
        .from(table)
        .select('id, title, status, lister_id, lister_role, lister_whatsapp, agreement_url, created_at');
      if (!fetchError && data) {
        all = all.concat(data.map((d: any) => ({ ...d, table })));
      }
    }
    // cars has no agreement_url column, so it's queried separately without it
    const { data: carData, error: carError } = await supabase
      .from('cars')
      .select('id, title, status, lister_id, lister_role, lister_whatsapp, created_at');
    if (!carError && carData) {
      all = all.concat(carData.map((d: any) => ({ ...d, table: 'cars' as const })));
    }
    setListings(all);
    setLoadingListings(false);
  };

  const fetchListers = async () => {
    setLoadingListers(true);
    const { data, error: fetchError } = await supabase
      .from('listers')
      .select('id, email, whatsapp, role, banned, balance, last_paid_at, created_at')
      .order('created_at', { ascending: false });
    if (!fetchError && data) {
      setListers(data as ListerRow[]);
    }
    setLoadingListers(false);
  };

  const fetchPaymentRequests = async () => {
    setLoadingPayments(true);
    const { data, error: fetchError } = await supabase
      .from('unlock_requests')
      .select('id, reference_code, listing_table, listing_title, buyer_phone, amount, screenshot_path, status, payment_method, created_at')
      .order('created_at', { ascending: false });
    if (!fetchError && data) {
      setPaymentRequests(data as PaymentRequest[]);
    }
    setLoadingPayments(false);
  };

  const fetchBlockedListers = async () => {
    setLoadingBlocked(true);
    const { data, error: fetchError } = await supabase
      .from('blocked_listers')
      .select('id, email, whatsapp, reason, blocked_at')
      .order('blocked_at', { ascending: false });
    if (!fetchError && data) {
      setBlockedListers(data as BlockedLister[]);
    }
    setLoadingBlocked(false);
  };

  const handleUnblockLister = async (blocked: BlockedLister) => {
    if (!window.confirm(`Unblock ${blocked.email || blocked.whatsapp}? They'll be able to sign up again.`)) return;
    const { error: deleteError } = await supabase.from('blocked_listers').delete().eq('id', blocked.id);
    if (deleteError) {
      alert('Error unblocking: ' + deleteError.message);
    } else {
      fetchBlockedListers();
    }
  };

  const fetchListerStats = async () => {
    setLoadingStats(true);
    const tables: ListingRow['table'][] = ['rentals', 'land', 'houses_for_sale', 'cars'];
    const listerWhatsapp: Record<string, string> = {};
    const listerListingCount: Record<string, number> = {};

    for (const table of tables) {
      const { data } = await supabase
        .from(table)
        .select('lister_id, lister_whatsapp')
        .not('lister_id', 'is', null);
      if (data) {
        data.forEach((row: any) => {
          if (row.lister_id) {
            listerWhatsapp[row.lister_id] = row.lister_whatsapp || 'Unknown';
            listerListingCount[row.lister_id] = (listerListingCount[row.lister_id] || 0) + 1;
          }
        });
      }
    }

    const { data: clickData } = await supabase
      .from('link_clicks')
      .select('lister_id')
      .not('lister_id', 'is', null);

    const clickCounts: Record<string, number> = {};
    if (clickData) {
      clickData.forEach((c: any) => {
        if (c.lister_id) clickCounts[c.lister_id] = (clickCounts[c.lister_id] || 0) + 1;
      });
    }

    const stats: ListerStat[] = Object.keys(listerWhatsapp).map(listerId => ({
      lister_id: listerId,
      whatsapp: listerWhatsapp[listerId],
      totalClicks: clickCounts[listerId] || 0,
      listingCount: listerListingCount[listerId] || 0,
    })).sort((a, b) => b.totalClicks - a.totalClicks);

    setListerStats(stats);
    setLoadingStats(false);
  };

  const handleBanListing = async (row: ListingRow) => {
    const { error: updateError } = await supabase.from(row.table).update({ status: 'banned' }).eq('id', row.id);
    if (updateError) {
      alert('Error hiding listing: ' + updateError.message);
    } else {
      fetchAllListings();
    }
  };

  const handleUnbanListing = async (row: ListingRow) => {
    const { error: updateError } = await supabase.from(row.table).update({ status: 'live' }).eq('id', row.id);
    if (updateError) {
      alert('Error restoring listing: ' + updateError.message);
    } else {
      fetchAllListings();
    }
  };

  const handleDeleteListing = async (row: ListingRow) => {
    if (!window.confirm(`Permanently delete "${row.title}"? This also removes its photos from storage. This cannot be undone.`)) return;

    // Grab the photo/agreement URLs before the row is gone, so we can clean up storage too.
    const { data: fullRow } = await supabase.from(row.table).select('images, agreement_url').eq('id', row.id).single();

    const { error: deleteError } = await supabase.from(row.table).delete().eq('id', row.id);
    if (deleteError) {
      alert('Error deleting: ' + deleteError.message);
      return;
    }

    if (fullRow?.images && Array.isArray(fullRow.images)) {
      await Promise.all(fullRow.images.map((url: string) => deleteStorageFile('rental-images', url)));
    }
    if (fullRow?.agreement_url) {
      await deleteStorageFile('lister-agreements', fullRow.agreement_url);
    }

    fetchAllListings();
  };

  const handleBanLister = async (lister: ListerRow) => {
    if (!window.confirm(`Suspend ${lister.email}? This also hides all of their current listings.`)) return;
    const { error: updateError } = await supabase.from('listers').update({ banned: true }).eq('id', lister.id);
    if (updateError) {
      alert('Error suspending lister: ' + updateError.message);
      return;
    }
    const tables: ListingRow['table'][] = ['rentals', 'land', 'houses_for_sale', 'cars'];
    for (const table of tables) {
      await supabase.from(table).update({ status: 'banned' }).eq('lister_id', lister.id);
    }
    fetchListers();
    fetchAllListings();
  };

  const handleUnbanLister = async (lister: ListerRow) => {
    const { error: updateError } = await supabase.from('listers').update({ banned: false }).eq('id', lister.id);
    if (updateError) {
      alert('Error unsuspending lister: ' + updateError.message);
    } else {
      fetchListers();
    }
  };

  const handleDeleteLister = async (lister: ListerRow) => {
    if (!window.confirm(`Permanently delete ${lister.email}? This removes their account, login, and ALL their listings, and blocks them from signing up again. This cannot be undone.`)) return;
    const { data, error: fnError } = await supabase.functions.invoke('delete-lister', {
      body: { lister_id: lister.id },
    });
    if (fnError || !data?.success) {
      alert('Error deleting lister: ' + (data?.error || fnError?.message || 'unknown error'));
    } else {
      fetchListers();
      fetchAllListings();
      fetchBlockedListers();
    }
  };

  const handleAddEarnings = async (lister: ListerRow) => {
    const amountStr = earningsInput[lister.id];
    const amount = parseFloat(amountStr);
    if (!amount || amount <= 0) {
      alert('Enter a valid amount to add first.');
      return;
    }
    const { error: updateError } = await supabase.from('listers')
      .update({ balance: (lister.balance || 0) + amount })
      .eq('id', lister.id);
    if (updateError) {
      alert('Error adding earnings: ' + updateError.message);
    } else {
      setEarningsInput(prev => ({ ...prev, [lister.id]: '' }));
      fetchListers();
    }
  };

  const handleMarkPaid = async (lister: ListerRow) => {
    if (!window.confirm(`Mark KSh ${lister.balance.toLocaleString()} as paid out to ${lister.email}? This resets their balance to 0.`)) return;
    const { error: updateError } = await supabase.from('listers')
      .update({ balance: 0, last_paid_at: new Date().toISOString() })
      .eq('id', lister.id);
    if (updateError) {
      alert('Error marking paid: ' + updateError.message);
    } else {
      fetchListers();
    }
  };

  const handleVerifyPayment = async (req: PaymentRequest) => {
    if (!window.confirm(`Confirm you've checked M-Pesa and KSh ${req.amount} really arrived from ${req.buyer_phone}?`)) return;
    const { error: updateError } = await supabase.from('unlock_requests')
      .update({ status: 'verified', verified_at: new Date().toISOString() })
      .eq('id', req.id);
    if (updateError) {
      alert('Error verifying: ' + updateError.message);
    } else {
      fetchPaymentRequests();
    }
  };

  const handleRejectPayment = async (req: PaymentRequest) => {
    if (!window.confirm(`Reject this payment request from ${req.buyer_phone}?`)) return;
    const { error: updateError } = await supabase.from('unlock_requests')
      .update({ status: 'rejected' })
      .eq('id', req.id);
    if (updateError) {
      alert('Error rejecting: ' + updateError.message);
    } else {
      fetchPaymentRequests();
    }
  };

  const handleDeletePaymentRequest = async (req: PaymentRequest) => {
    if (!window.confirm(`Delete this payment request (${req.reference_code})? This cannot be undone.`)) return;
    const { error: deleteError } = await supabase.from('unlock_requests').delete().eq('id', req.id);
    if (deleteError) {
      alert('Error deleting: ' + deleteError.message);
    } else {
      fetchPaymentRequests();
    }
  };

  const downloadCsv = (filename: string, rows: string[][]) => {
    const csv = rows.map(row =>
      row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPaymentsCsv = () => {
    const rows = [
      ['Reference', 'Listing', 'Category', 'Buyer Phone', 'Amount (KSh)', 'Method', 'Status', 'Date'],
      ...paymentRequests.map(r => [
        r.reference_code, r.listing_title || '', r.listing_table, r.buyer_phone,
        String(r.amount), r.payment_method, r.status, new Date(r.created_at).toLocaleString(),
      ]),
    ];
    downloadCsv(`payment-requests-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  const handleDownloadListersCsv = () => {
    const rows = [
      ['Email', 'WhatsApp', 'Role', 'Status', '# Listings', 'Listings', 'Balance Owed (KSh)', 'Joined'],
      ...listers.map(l => {
        const theirListings = listings.filter(row => row.lister_id === l.id);
        return [
          l.email, l.whatsapp, l.role, l.banned ? 'Suspended' : 'Active',
          String(theirListings.length), theirListings.map(li => li.title).join('; '),
          String(l.balance || 0), l.created_at ? new Date(l.created_at).toLocaleDateString() : '',
        ];
      }),
    ];
    downloadCsv(`listers-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  const handleViewScreenshot = async (path: string) => {
    const { data, error: signError } = await supabase.storage.from('payment-proofs').createSignedUrl(path, 60);
    if (signError || !data) {
      alert('Could not open screenshot: ' + (signError?.message || 'unknown error'));
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleLogin = async () => {
    if (blocked) return;
    setError('');
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }
    if (email.toLowerCase() !== ADMIN_EMAIL) {
      // Don't burn a Supabase login attempt (or reveal anything) on an
      // email that could never be the admin account.
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      if (newAttempts >= 3) {
        setBlocked(true);
        setError('🔒 Too many failed attempts. Access blocked. Refresh to try again.');
      } else {
        setError(`That email/password combination doesn't match our records. ${3 - newAttempts} attempt${3 - newAttempts === 1 ? '' : 's'} remaining.`);
      }
      return;
    }
    const { error: loginError } = await supabase.auth.signInWithPassword({ email, password });
    if (loginError) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      if (newAttempts >= 3) {
        setBlocked(true);
        setError('🔒 Too many failed attempts. Access blocked. Refresh to try again.');
      } else {
        setError(`That email/password combination doesn't match our records. ${3 - newAttempts} attempt${3 - newAttempts === 1 ? '' : 's'} remaining.`);
      }
    } else {
      setAttempts(0);
    }
  };

  const handleMagicLink = async () => {
    setError('');
    if (email.toLowerCase() !== ADMIN_EMAIL) {
      setError('That email doesn\u2019t match the admin account.');
      return;
    }
    setLoading(true);
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/oss-sntm`,
      },
    });
    setLoading(false);
    if (otpError) {
      setError(otpError.message);
      return;
    }
    setMagicLinkSent(true);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setPassword('');
    setEmail('');
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const max = activeTab === 'land' ? 10 : 7;
    if (files.length > max) { setError(`Maximum ${max} images!`); return; }
    setImages(files);
    setPreviews(files.map(f => URL.createObjectURL(f)));
  };

  const uploadImages = async () => {
    const urls: string[] = [];
    for (let i = 0; i < images.length; i++) {
      const file = images[i];
      const fileName = `${Date.now()}-${i}-${file.name}`;
      const { error: uploadError } = await supabase.storage.from('rental-images').upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from('rental-images').getPublicUrl(fileName);
      urls.push(data.publicUrl);
    }
    return urls;
  };

  const uploadAgreement = async () => {
    if (!agreementFile) return null;
    const fileName = `${Date.now()}-${agreementFile.name}`;
    const { error: uploadError } = await supabase.storage.from('lister-agreements').upload(fileName, agreementFile);
    if (uploadError) throw uploadError;
    // Private bucket — store just the file path. A real link is generated
    // on demand (see handleViewAgreement) since public URLs don't work here.
    return fileName;
  };

  const handleViewAgreement = async (path: string) => {
    const { data, error: signError } = await supabase.storage.from('lister-agreements').createSignedUrl(path, 60);
    if (signError || !data) {
      alert('Could not open agreement: ' + (signError?.message || 'unknown error'));
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };
 
  const resetForm = () => {
    setImages([]); setPreviews([]); setSuccess(''); setError('');
    setAgreementFile(null); setVideoUrl('');
    setListerForm({ role: 'Caretaker', whatsapp: '' });
    setRentalForm({ title: '', building_name: '', type: 'Bedsitter', bedrooms: 0, price_per_month: '', description: '', whatsapp: '', google_maps_link: '' });
    setLandForm({
      title: '', county: '', location: '', size: '', price: '', type: 'Residential', description: '',
      whatsapp: '', second_whatsapp: '', google_maps_link: '', agent_name: '',
      water_on_site: '', water_in_area: '', electricity_on_site: '', electricity_in_area: '',
      title_deed: '', document_type: '', access_road_type: '', distance_to_tarmac: '',
      has_disputes: '', beacons_confirmed: '', nearby_amenities: '', negotiable: '', payment_plan_available: '',
    });
    setHouseForm({ title: '', building_name: '', location: '', price: '', bedrooms: '', bathrooms: '', type: 'Apartment', description: '', whatsapp: '', google_maps_link: '' });
    setCarForm({ title: '', make: '', model: '', year: '', mileage: '', price: '', type: 'Sedan', transmission: 'Automatic', location: '', description: '', whatsapp: '' });
  };

  const handleSubmit = async () => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const imageUrls = await uploadImages();
      const agreementUrl = await uploadAgreement();
      const listerData = { lister_role: listerForm.role, lister_whatsapp: listerForm.whatsapp, agreement_url: agreementUrl, status: 'live' };

      if (activeTab === 'rental') {
        const { error: e } = await supabase.from('rentals').insert({ ...rentalForm, price_per_month: parseInt(rentalForm.price_per_month), unlock_fee: unlockFees[rentalForm.type], images: imageUrls, video_url: videoUrl || null, ...listerData });
        if (e) throw e;
      } else if (activeTab === 'land') {
        const toBool = (v: string) => v === 'Yes' ? true : v === 'No' ? false : null;
        const { error: e } = await supabase.from('land').insert({
          title: landForm.title,
          county: landForm.county,
          location: landForm.location,
          size: landForm.size,
          price: parseInt(landForm.price),
          type: landForm.type,
          description: landForm.description,
          whatsapp: landForm.whatsapp,
          second_whatsapp: landForm.second_whatsapp || null,
          google_maps_link: landForm.google_maps_link,
          agent_name: landForm.agent_name || null,
          water_on_site: toBool(landForm.water_on_site),
          water_in_area: toBool(landForm.water_in_area),
          electricity_on_site: toBool(landForm.electricity_on_site),
          electricity_in_area: toBool(landForm.electricity_in_area),
          title_deed: toBool(landForm.title_deed),
          document_type: landForm.document_type || null,
          access_road_type: landForm.access_road_type || null,
          distance_to_tarmac: landForm.distance_to_tarmac || null,
          has_disputes: toBool(landForm.has_disputes),
          beacons_confirmed: toBool(landForm.beacons_confirmed),
          nearby_amenities: landForm.nearby_amenities || null,
          negotiable: toBool(landForm.negotiable),
          payment_plan_available: toBool(landForm.payment_plan_available),
          unlock_fee: 150,
          images: imageUrls,
          video_url: videoUrl || null,
          ...listerData,
        });
        if (e) throw e;
      } else if (activeTab === 'house') {
        const { error: e } = await supabase.from('houses_for_sale').insert({ ...houseForm, price: parseInt(houseForm.price), bedrooms: parseInt(houseForm.bedrooms), bathrooms: parseInt(houseForm.bathrooms), images: imageUrls, video_url: videoUrl || null, ...listerData });
        if (e) throw e;
      } else if (activeTab === 'car') {
        const { error: e } = await supabase.from('cars').insert({ ...carForm, year: parseInt(carForm.year), price: parseInt(carForm.price), images: imageUrls, lister_role: listerForm.role, lister_whatsapp: listerForm.whatsapp, status: 'live', video_url: videoUrl || null });
        if (e) throw e;
      }
      setSuccess('✅ Listing added successfully!');
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Something went wrong!');
    }
    setLoading(false);
  };

  if (checkingSession) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'var(--dark-3)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '48px', width: '100%', maxWidth: '400px' }}>
          {mode === 'forgot' ? (
            <ForgotPasswordPanel defaultEmail={email} onBack={() => setMode('login')} />
          ) : mode === 'magiclink' ? (
            magicLinkSent ? (
              <>
                <h1 className="serif" style={{ color: 'var(--gold)', fontSize: '26px', fontWeight: 300, marginBottom: '16px' }}>Check Your Email</h1>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', lineHeight: 1.6, marginBottom: '20px' }}>
                  We've sent a magic link to <strong style={{ color: 'var(--text)' }}>{email}</strong>. Click it to log in.
                </p>
                <button onClick={() => { setMagicLinkSent(false); setMode('login'); }} className="btn-ghost" style={{ width: '100%' }}>
                  Back to Login
                </button>
              </>
            ) : (
              <>
                <h1 className="serif" style={{ color: 'var(--gold)', fontSize: '28px', fontWeight: 300, marginBottom: '8px' }}>Magic Link Login</h1>
                <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '32px' }}>One Stop Shop — Private</p>
                <input type="email" placeholder="Admin email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  style={{ ...inputStyle, marginBottom: '16px' }} />
                {error && <p style={{ color: '#f97316', fontSize: '13px', marginBottom: '16px' }}>{error}</p>}
                <button onClick={handleMagicLink} className="btn-primary" style={{ width: '100%', marginBottom: '16px' }} disabled={loading}>
                  {loading ? 'Sending...' : 'Send Magic Link'}
                </button>
                <button onClick={() => setMode('login')} style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--gold)', fontSize: '12px', cursor: 'pointer' }}>
                  ← Use password instead
                </button>
              </>
            )
          ) : (
            <>
              <h1 className="serif" style={{ color: 'var(--gold)', fontSize: '28px', fontWeight: 300, marginBottom: '8px' }}>Admin Access</h1>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '32px' }}>One Stop Shop — Private</p>
              <input type="email" placeholder="Admin email" value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={blocked}
                style={{ ...inputStyle, marginBottom: '16px', opacity: blocked ? 0.5 : 1 }} />
              <PasswordInput value={password} onChange={setPassword}
                placeholder="Enter password"
                onEnter={() => !blocked && handleLogin()}
                disabled={blocked}
                style={{ marginBottom: '8px' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <button onClick={() => setMode('magiclink')} disabled={blocked}
                  style={{ background: 'transparent', border: 'none', color: 'var(--gold)', fontSize: '12px', cursor: blocked ? 'default' : 'pointer', padding: 0, opacity: blocked ? 0.5 : 1 }}>
                  Use magic link instead
                </button>
                <button onClick={() => setMode('forgot')} disabled={blocked}
                  style={{ background: 'transparent', border: 'none', color: 'var(--gold)', fontSize: '12px', cursor: blocked ? 'default' : 'pointer', padding: 0, opacity: blocked ? 0.5 : 1 }}>
                  Forgot password?
                </button>
              </div>
              {error && (
                <p style={{ color: blocked ? '#f87171' : '#f97316', fontSize: '13px', marginBottom: '16px', fontWeight: blocked ? 600 : 400 }}>
                  {error}
                </p>
              )}
              <button onClick={handleLogin} className="btn-primary" style={{ width: '100%', opacity: blocked ? 0.5 : 1 }} disabled={blocked}>
                {blocked ? '🔒 Access Blocked' : 'Login'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const pendingPaymentsCount = paymentRequests.filter(p => p.status === 'pending').length;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--dark)', paddingTop: '100px', paddingBottom: '80px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <p style={{ color: 'var(--gold)', fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase' }}>Admin Panel</p>
          <button onClick={handleSignOut}
            style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
            Sign Out
          </button>
        </div>

        <h1 className="serif" style={{ fontSize: '42px', fontWeight: 300, color: 'var(--text)', marginBottom: '32px' }}>Admin <span style={{ color: 'var(--gold)' }}>Panel</span></h1>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '40px', flexWrap: 'wrap' }}>
          <button onClick={() => setMainView('add')}
            style={{ padding: '10px 20px', borderRadius: '50px', border: '1px solid rgba(201,168,76,0.3)', background: mainView === 'add' ? 'rgba(201,168,76,0.15)' : 'transparent', color: mainView === 'add' ? 'var(--gold)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
            ➕ Add Listing
          </button>
          <button onClick={() => { setMainView('listings'); setFilterListerId(null); }}
            style={{ padding: '10px 20px', borderRadius: '50px', border: '1px solid rgba(201,168,76,0.3)', background: mainView === 'listings' ? 'rgba(201,168,76,0.15)' : 'transparent', color: mainView === 'listings' ? 'var(--gold)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
            📋 Manage Listings
          </button>
          <button onClick={() => setMainView('listers')}
            style={{ padding: '10px 20px', borderRadius: '50px', border: '1px solid rgba(201,168,76,0.3)', background: mainView === 'listers' ? 'rgba(201,168,76,0.15)' : 'transparent', color: mainView === 'listers' ? 'var(--gold)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
            👤 Manage Listers
            {listers.filter(l => l.banned).length > 0 && (
              <span style={{ background: '#f87171', color: '#000', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '50px' }}>
                {listers.filter(l => l.banned).length} suspended
              </span>
            )}
          </button>
          <button onClick={() => setMainView('payments')}
            style={{ padding: '10px 20px', borderRadius: '50px', border: '1px solid rgba(201,168,76,0.3)', background: mainView === 'payments' ? 'rgba(201,168,76,0.15)' : 'transparent', color: mainView === 'payments' ? 'var(--gold)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
            💳 Payment Requests
            {pendingPaymentsCount > 0 && (
              <span style={{ background: '#f97316', color: '#000', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '50px' }}>
                {pendingPaymentsCount} pending
              </span>
            )}
          </button>
          <button onClick={() => { setMainView('analytics'); fetchListerStats(); }}
            style={{ padding: '10px 20px', borderRadius: '50px', border: '1px solid rgba(201,168,76,0.3)', background: mainView === 'analytics' ? 'rgba(201,168,76,0.15)' : 'transparent', color: mainView === 'analytics' ? 'var(--gold)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
            📊 Link Analytics
          </button>
        </div>

        {mainView === 'payments' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', alignSelf: 'flex-end' }}>
              <button onClick={handleDownloadPaymentsCsv}
                style={{ background: 'transparent', border: '1px solid rgba(201,168,76,0.3)', color: 'var(--gold)', padding: '6px 14px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                ⬇ Download CSV
              </button>
              <button onClick={fetchPaymentRequests}
                style={{ background: 'transparent', border: '1px solid rgba(201,168,76,0.3)', color: 'var(--gold)', padding: '6px 14px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                🔄 Refresh
              </button>
            </div>
            {loadingPayments && <p style={{ color: 'var(--text-muted)' }}>Loading...</p>}
            {!loadingPayments && paymentRequests.length === 0 && (
              <p style={{ color: 'var(--text-muted)' }}>No payment requests yet.</p>
            )}
            {paymentRequests.map(req => (
              <div key={req.id} style={{ background: 'rgba(201,168,76,0.03)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: '8px', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <p style={{ color: 'var(--text)', fontSize: '15px', fontWeight: 500 }}>
                      {req.listing_title || 'Listing'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {req.listing_table}</span>
                    </p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                      Ref <strong style={{ color: 'var(--gold)' }}>{req.reference_code}</strong> · {req.buyer_phone} · KSh {req.amount} · {req.payment_method === 'stk' ? '📲 Auto (STK)' : '✍️ Manual'}
                    </p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px' }}>
                      {new Date(req.created_at).toLocaleString()}
                    </p>
                  </div>
                  <span style={{
                    fontSize: '11px', padding: '4px 10px', borderRadius: '50px', whiteSpace: 'nowrap',
                    background: req.status === 'verified' ? 'rgba(74,222,128,0.15)' : req.status === 'rejected' ? 'rgba(248,113,113,0.15)' : 'rgba(249,115,22,0.15)',
                    color: req.status === 'verified' ? '#4ade80' : req.status === 'rejected' ? '#f87171' : '#f97316',
                  }}>
                    {req.status === 'verified' ? 'Verified' : req.status === 'rejected' ? 'Rejected' : 'Pending'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {req.screenshot_path && (
                    <button onClick={() => handleViewScreenshot(req.screenshot_path!)}
                      style={{ background: 'transparent', border: '1px solid rgba(201,168,76,0.3)', color: 'var(--gold)', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                      📷 View Screenshot
                    </button>
                  )}
                  {req.status === 'pending' && (
                    <>
                      <button onClick={() => handleVerifyPayment(req)}
                        style={{ background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.3)', color: '#4ade80', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
                        ✓ Verify
                      </button>
                      <button onClick={() => handleRejectPayment(req)}
                        style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                        ✕ Reject
                      </button>
                    </>
                  )}
                  <button onClick={() => handleDeletePaymentRequest(req)}
                    style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', opacity: 0.7 }}>
                    🗑 Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {mainView === 'listings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filterListerId && (() => {
              const filteredLister = listers.find(l => l.id === filterListerId);
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '6px', padding: '10px 16px', marginBottom: '4px' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                    Showing listings by <strong style={{ color: 'var(--gold)' }}>{filteredLister?.email || 'this lister'}</strong>
                  </span>
                  <button onClick={() => setFilterListerId(null)}
                    style={{ background: 'transparent', border: 'none', color: 'var(--gold)', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>
                    Clear filter
                  </button>
                </div>
              );
            })()}
            {loadingListings && <p style={{ color: 'var(--text-muted)' }}>Loading...</p>}
            {!loadingListings && listings.filter(row => !filterListerId || row.lister_id === filterListerId).length === 0 && (
              <p style={{ color: 'var(--text-muted)' }}>{filterListerId ? 'This lister has no listings yet.' : 'No listings yet.'}</p>
            )}
            {listings.filter(row => !filterListerId || row.lister_id === filterListerId).map(row => (
              <div key={`${row.table}-${row.id}`} style={{ background: 'rgba(201,168,76,0.03)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <p style={{ color: 'var(--text)', fontSize: '15px', fontWeight: 500 }}>{row.title}</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                    {row.table.replace(/_/g, ' ')} · {row.lister_id ? `Listed by ${row.lister_role || 'Unknown'} (${row.lister_whatsapp || 'no contact'})` : 'Added by admin'}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '11px', padding: '4px 10px', borderRadius: '50px',
                    background: row.status === 'banned' ? 'rgba(248,113,113,0.15)' : 'rgba(74,222,128,0.15)',
                    color: row.status === 'banned' ? '#f87171' : '#4ade80',
                  }}>
                    {row.status === 'banned' ? 'Hidden' : 'Live'}
                  </span>
                  {row.agreement_url && (
                    <button onClick={() => handleViewAgreement(row.agreement_url!)}
                      style={{ background: 'transparent', border: '1px solid rgba(201,168,76,0.3)', color: 'var(--gold)', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                      📄 Agreement
                    </button>
                  )}
                  {row.status === 'banned' ? (
                    <button onClick={() => handleUnbanListing(row)}
                      style={{ background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.3)', color: '#4ade80', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
                      ✓ Restore
                    </button>
                  ) : (
                    <button onClick={() => handleBanListing(row)}
                      style={{ background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.3)', color: '#f97316', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
                      🚫 Hide
                    </button>
                  )}
                  <button onClick={() => handleDeleteListing(row)}
                    style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                    🗑 Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {mainView === 'listers' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button onClick={handleDownloadListersCsv}
              style={{ alignSelf: 'flex-end', background: 'transparent', border: '1px solid rgba(201,168,76,0.3)', color: 'var(--gold)', padding: '6px 14px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
              ⬇ Download CSV
            </button>
            {loadingListers && <p style={{ color: 'var(--text-muted)' }}>Loading...</p>}
            {!loadingListers && listers.length === 0 && (
              <p style={{ color: 'var(--text-muted)' }}>No self-registered listers yet.</p>
            )}
            {listers.map(lister => (
              <div key={lister.id} style={{ background: 'rgba(201,168,76,0.03)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: '8px', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <p onClick={() => { setFilterListerId(lister.id); setMainView('listings'); }}
                      style={{ color: 'var(--text)', fontSize: '15px', fontWeight: 500, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(201,168,76,0.4)' }}>
                      {lister.email}
                    </p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                      {lister.role} · {lister.whatsapp || 'no contact'} · {listings.filter(row => row.lister_id === lister.id).length} listing{listings.filter(row => row.lister_id === lister.id).length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontSize: '11px', padding: '4px 10px', borderRadius: '50px',
                      background: lister.banned ? 'rgba(248,113,113,0.15)' : 'rgba(74,222,128,0.15)',
                      color: lister.banned ? '#f87171' : '#4ade80',
                    }}>
                      {lister.banned ? 'Suspended' : 'Active'}
                    </span>
                    {lister.banned ? (
                      <button onClick={() => handleUnbanLister(lister)}
                        style={{ background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.3)', color: '#4ade80', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
                        ✓ Reinstate
                      </button>
                    ) : (
                      <button onClick={() => handleBanLister(lister)}
                        style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                        🚫 Suspend
                      </button>
                    )}
                    <button onClick={() => handleDeleteLister(lister)}
                      style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', opacity: 0.7 }}>
                      🗑 Delete
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', borderTop: '1px solid rgba(201,168,76,0.1)', paddingTop: '12px' }}>
                  <div>
                    <p style={{ color: 'var(--text-muted)', fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Balance owed</p>
                    <p style={{ color: 'var(--gold)', fontSize: '18px', fontWeight: 600 }}>
                      KSh {(lister.balance || 0).toLocaleString()}
                      {lister.last_paid_at && (
                        <span style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: 400, marginLeft: '8px' }}>
                          last paid {new Date(lister.last_paid_at).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="number"
                      placeholder="Amount"
                      value={earningsInput[lister.id] || ''}
                      onChange={e => setEarningsInput(prev => ({ ...prev, [lister.id]: e.target.value }))}
                      style={{ ...inputStyle, width: '110px', padding: '8px 12px', fontSize: '12px' }}
                    />
                    <button onClick={() => handleAddEarnings(lister)}
                      style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: 'var(--gold)', padding: '8px 14px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
                      + Add
                    </button>
                    <button onClick={() => handleMarkPaid(lister)}
                      disabled={!lister.balance}
                      style={{ background: 'transparent', border: '1px solid rgba(74,222,128,0.3)', color: '#4ade80', padding: '8px 14px', borderRadius: '4px', fontSize: '12px', cursor: lister.balance ? 'pointer' : 'default', opacity: lister.balance ? 1 : 0.4 }}>
                      Mark Paid
                    </button>
                  </div>
                </div>
              </div>
            ))}

            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid rgba(201,168,76,0.1)' }}>
              <p style={{ color: 'var(--gold)', fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px', fontWeight: 600 }}>
                🚫 Blocked ({blockedListers.length})
              </p>
              {loadingBlocked && <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading...</p>}
              {!loadingBlocked && blockedListers.length === 0 && (
                <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No one is currently blocked.</p>
              )}
              {blockedListers.map(b => (
                <div key={b.id} style={{ background: 'rgba(248,113,113,0.03)', border: '1px solid rgba(248,113,113,0.15)', borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '8px' }}>
                  <div>
                    <p style={{ color: 'var(--text)', fontSize: '13px' }}>{b.email || '—'} {b.whatsapp ? `· ${b.whatsapp}` : ''}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px' }}>
                      {b.reason || 'Blocked'} · {new Date(b.blocked_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button onClick={() => handleUnblockLister(b)}
                    style={{ background: 'transparent', border: '1px solid rgba(74,222,128,0.3)', color: '#4ade80', padding: '6px 14px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                    Unblock
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {mainView === 'analytics' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '8px' }}>
              Shows how many visits each lister's shared links have generated, based on people clicking their "Share this listing" links.
            </p>
            {loadingStats && <p style={{ color: 'var(--text-muted)' }}>Loading...</p>}
            {!loadingStats && listerStats.length === 0 && (
              <p style={{ color: 'var(--text-muted)' }}>No lister activity yet.</p>
            )}
            {listerStats.map(stat => (
              <div key={stat.lister_id} style={{ background: 'rgba(201,168,76,0.03)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ color: 'var(--text)', fontSize: '15px', fontWeight: 500 }}>{stat.whatsapp}</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>{stat.listingCount} listing{stat.listingCount === 1 ? '' : 's'}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ color: 'var(--gold)', fontSize: '22px', fontWeight: 600 }}>{stat.totalClicks}</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '11px' }}>shared views</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {mainView === 'add' && <>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '40px', flexWrap: 'wrap' }}>
          {[
            { key: 'rental', label: '🔑 Rental' },
            { key: 'land', label: '🌍 Land' },
            { key: 'house', label: '🏠 House for Sale' },
            { key: 'car', label: '🚗 Car' },
          ].map(tab => (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key as any); resetForm(); }}
              style={{ padding: '10px 20px', borderRadius: '50px', border: '1px solid rgba(201,168,76,0.3)', background: activeTab === tab.key ? 'rgba(201,168,76,0.15)' : 'transparent', color: activeTab === tab.key ? 'var(--gold)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {activeTab === 'rental' && <>
            <div><label style={labelStyle}>Listing Title</label><input value={rentalForm.title} onChange={e => setRentalForm({ ...rentalForm, title: e.target.value })} placeholder="e.g. Modern Bedsitter - Kilimani" style={inputStyle} /></div>
            <div><label style={labelStyle}>Building Name</label><input value={rentalForm.building_name} onChange={e => setRentalForm({ ...rentalForm, building_name: e.target.value })} placeholder="e.g. Sunrise Apartments" style={inputStyle} /></div>
            <div><label style={labelStyle}>House Type</label>
              <select value={rentalForm.type} onChange={e => setRentalForm({ ...rentalForm, type: e.target.value })} style={inputStyle}>
                {Object.keys(unlockFees).map(t => <option key={t} value={t}>{t} — Unlock: KSh {unlockFees[t]}</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Monthly Rent (KSh)</label><input type="number" value={rentalForm.price_per_month} onChange={e => setRentalForm({ ...rentalForm, price_per_month: e.target.value })} placeholder="e.g. 15000" style={inputStyle} /></div>
            <div><label style={labelStyle}>Description</label><textarea value={rentalForm.description} onChange={e => setRentalForm({ ...rentalForm, description: e.target.value })} placeholder="Describe the property..." rows={4} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            <div><label style={labelStyle}>Caretaker WhatsApp</label><input value={rentalForm.whatsapp} onChange={e => setRentalForm({ ...rentalForm, whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} /></div>
            <div><label style={labelStyle}>Google Maps Link</label><input value={rentalForm.google_maps_link} onChange={e => setRentalForm({ ...rentalForm, google_maps_link: e.target.value })} placeholder="Paste Google Maps link" style={inputStyle} />
              <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '6px' }}>💡 Google Maps → search building → Share → Copy Link</p>
            </div>
          </>}

          {activeTab === 'land' && <>
            <div style={{ background: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.15)', borderRadius: '6px', padding: '14px 16px' }}>
              <p style={{ color: '#f87171', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Land Listing Rules</p>
              <p style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                1. False or misleading information → indefinite ban.<br />
                2. Forfeiture of all accumulated funds.<br />
                3. A public ban notice will name the listing as fraudulent.<br />
                4. Listed price must be genuine — no bait pricing.
              </p>
            </div>
            <div><label style={labelStyle}>Listing Title <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(do not mention your company name)</span></label><input value={landForm.title} onChange={e => setLandForm({ ...landForm, title: e.target.value })} placeholder="e.g. Prime Plot - Kiambu" style={inputStyle} /></div>
            <div><label style={labelStyle}>County</label><input value={landForm.county} onChange={e => setLandForm({ ...landForm, county: e.target.value })} placeholder="e.g. Kiambu" style={inputStyle} /></div>
            <div><label style={labelStyle}>Location</label><input value={landForm.location} onChange={e => setLandForm({ ...landForm, location: e.target.value })} placeholder="e.g. Ruiru, Juja Farm, Kilimambogo" style={inputStyle} /></div>
            <div><label style={labelStyle}>Size</label><input value={landForm.size} onChange={e => setLandForm({ ...landForm, size: e.target.value })} placeholder="e.g. 50x100 ft or 2 Acres" style={inputStyle} /></div>
            <div><label style={labelStyle}>Price (KSh)</label><input type="number" value={landForm.price} onChange={e => setLandForm({ ...landForm, price: e.target.value })} placeholder="e.g. 2500000" style={inputStyle} /></div>
            <div><label style={labelStyle}>Type</label>
              <select value={landForm.type} onChange={e => setLandForm({ ...landForm, type: e.target.value })} style={inputStyle}>
                {['Residential', 'Agricultural', 'Commercial', 'Beach'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Description</label><textarea value={landForm.description} onChange={e => setLandForm({ ...landForm, description: e.target.value })} placeholder="Describe the land..." rows={4} style={{ ...inputStyle, resize: 'vertical' }} /></div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div><label style={labelStyle}>Water on-site?</label>
                <select value={landForm.water_on_site} onChange={e => setLandForm({ ...landForm, water_on_site: e.target.value })} style={inputStyle}>
                  <option value="">Not specified</option><option>Yes</option><option>No</option>
                </select>
              </div>
              <div><label style={labelStyle}>Water in the area?</label>
                <select value={landForm.water_in_area} onChange={e => setLandForm({ ...landForm, water_in_area: e.target.value })} style={inputStyle}>
                  <option value="">Not specified</option><option>Yes</option><option>No</option>
                </select>
              </div>
              <div><label style={labelStyle}>Electricity on-site?</label>
                <select value={landForm.electricity_on_site} onChange={e => setLandForm({ ...landForm, electricity_on_site: e.target.value })} style={inputStyle}>
                  <option value="">Not specified</option><option>Yes</option><option>No</option>
                </select>
              </div>
              <div><label style={labelStyle}>Electricity in the area?</label>
                <select value={landForm.electricity_in_area} onChange={e => setLandForm({ ...landForm, electricity_in_area: e.target.value })} style={inputStyle}>
                  <option value="">Not specified</option><option>Yes</option><option>No</option>
                </select>
              </div>
              <div><label style={labelStyle}>Title deed?</label>
                <select value={landForm.title_deed} onChange={e => setLandForm({ ...landForm, title_deed: e.target.value })} style={inputStyle}>
                  <option value="">Not specified</option><option>Yes</option><option>No</option>
                </select>
              </div>
              <div><label style={labelStyle}>Beacons confirmed?</label>
                <select value={landForm.beacons_confirmed} onChange={e => setLandForm({ ...landForm, beacons_confirmed: e.target.value })} style={inputStyle}>
                  <option value="">Not specified</option><option>Yes</option><option>No</option>
                </select>
              </div>
              <div><label style={labelStyle}>Any disputes/caveats?</label>
                <select value={landForm.has_disputes} onChange={e => setLandForm({ ...landForm, has_disputes: e.target.value })} style={inputStyle}>
                  <option value="">Not specified</option><option>Yes</option><option>No</option>
                </select>
              </div>
              <div><label style={labelStyle}>Negotiable?</label>
                <select value={landForm.negotiable} onChange={e => setLandForm({ ...landForm, negotiable: e.target.value })} style={inputStyle}>
                  <option value="">Not specified</option><option>Yes</option><option>No</option>
                </select>
              </div>
              <div><label style={labelStyle}>Payment plan available?</label>
                <select value={landForm.payment_plan_available} onChange={e => setLandForm({ ...landForm, payment_plan_available: e.target.value })} style={inputStyle}>
                  <option value="">Not specified</option><option>Yes</option><option>No</option>
                </select>
              </div>
            </div>

            <div><label style={labelStyle}>Document type <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(if not full title deed)</span></label><input value={landForm.document_type} onChange={e => setLandForm({ ...landForm, document_type: e.target.value })} placeholder="e.g. Allotment letter, Sale agreement" style={inputStyle} /></div>
            <div><label style={labelStyle}>Access road type</label><input value={landForm.access_road_type} onChange={e => setLandForm({ ...landForm, access_road_type: e.target.value })} placeholder="e.g. Tarmac, Murram, Footpath" style={inputStyle} /></div>
            <div><label style={labelStyle}>Distance to tarmac</label><input value={landForm.distance_to_tarmac} onChange={e => setLandForm({ ...landForm, distance_to_tarmac: e.target.value })} placeholder="e.g. 500m" style={inputStyle} /></div>
            <div><label style={labelStyle}>Nearby amenities</label><input value={landForm.nearby_amenities} onChange={e => setLandForm({ ...landForm, nearby_amenities: e.target.value })} placeholder="e.g. School, hospital, market, matatu stage" style={inputStyle} /></div>

            <div><label style={labelStyle}>Agent/Manager Name</label><input value={landForm.agent_name} onChange={e => setLandForm({ ...landForm, agent_name: e.target.value })} placeholder="Shown to buyers after they unlock" style={inputStyle} /></div>
            <div><label style={labelStyle}>Primary WhatsApp Contact</label><input value={landForm.whatsapp} onChange={e => setLandForm({ ...landForm, whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} /></div>
            <div><label style={labelStyle}>Second WhatsApp Contact (optional)</label><input value={landForm.second_whatsapp} onChange={e => setLandForm({ ...landForm, second_whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} /></div>
            <div><label style={labelStyle}>Google Maps Link</label><input value={landForm.google_maps_link} onChange={e => setLandForm({ ...landForm, google_maps_link: e.target.value })} placeholder="Paste Google Maps link" style={inputStyle} /></div>
          </>}

          {activeTab === 'house' && <>
            <div><label style={labelStyle}>Listing Title</label><input value={houseForm.title} onChange={e => setHouseForm({ ...houseForm, title: e.target.value })} placeholder="e.g. Modern Apartment - Westlands" style={inputStyle} /></div>
            <div><label style={labelStyle}>Building Name</label><input value={houseForm.building_name} onChange={e => setHouseForm({ ...houseForm, building_name: e.target.value })} placeholder="e.g. Greenpark Estate" style={inputStyle} /></div>
            <div><label style={labelStyle}>Location</label><input value={houseForm.location} onChange={e => setHouseForm({ ...houseForm, location: e.target.value })} placeholder="e.g. Westlands, Nairobi" style={inputStyle} /></div>
            <div><label style={labelStyle}>Price (KSh)</label><input type="number" value={houseForm.price} onChange={e => setHouseForm({ ...houseForm, price: e.target.value })} placeholder="e.g. 8500000" style={inputStyle} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div><label style={labelStyle}>Bedrooms</label><input type="number" value={houseForm.bedrooms} onChange={e => setHouseForm({ ...houseForm, bedrooms: e.target.value })} placeholder="e.g. 3" style={inputStyle} /></div>
              <div><label style={labelStyle}>Bathrooms</label><input type="number" value={houseForm.bathrooms} onChange={e => setHouseForm({ ...houseForm, bathrooms: e.target.value })} placeholder="e.g. 2" style={inputStyle} /></div>
            </div>
            <div><label style={labelStyle}>Type</label>
              <select value={houseForm.type} onChange={e => setHouseForm({ ...houseForm, type: e.target.value })} style={inputStyle}>
                {['Apartment', 'Bungalow', 'Townhouse', 'Mansion', 'Beach House'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Description</label><textarea value={houseForm.description} onChange={e => setHouseForm({ ...houseForm, description: e.target.value })} placeholder="Describe the property..." rows={4} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            <div><label style={labelStyle}>WhatsApp Contact</label><input value={houseForm.whatsapp} onChange={e => setHouseForm({ ...houseForm, whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} /></div>
            <div><label style={labelStyle}>Google Maps Link</label><input value={houseForm.google_maps_link} onChange={e => setHouseForm({ ...houseForm, google_maps_link: e.target.value })} placeholder="Paste Google Maps link" style={inputStyle} /></div>
          </>}

          {activeTab === 'car' && <>
            <div><label style={labelStyle}>Listing Title</label><input value={carForm.title} onChange={e => setCarForm({ ...carForm, title: e.target.value })} placeholder="e.g. Toyota Land Cruiser V8" style={inputStyle} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div><label style={labelStyle}>Make</label><input value={carForm.make} onChange={e => setCarForm({ ...carForm, make: e.target.value })} placeholder="e.g. Toyota" style={inputStyle} /></div>
              <div><label style={labelStyle}>Model</label><input value={carForm.model} onChange={e => setCarForm({ ...carForm, model: e.target.value })} placeholder="e.g. Land Cruiser" style={inputStyle} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div><label style={labelStyle}>Year</label><input type="number" value={carForm.year} onChange={e => setCarForm({ ...carForm, year: e.target.value })} placeholder="e.g. 2022" style={inputStyle} /></div>
              <div><label style={labelStyle}>Mileage</label><input value={carForm.mileage} onChange={e => setCarForm({ ...carForm, mileage: e.target.value })} placeholder="e.g. 15,000 km" style={inputStyle} /></div>
            </div>
            <div><label style={labelStyle}>Price (KSh)</label><input type="number" value={carForm.price} onChange={e => setCarForm({ ...carForm, price: e.target.value })} placeholder="e.g. 3500000" style={inputStyle} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div><label style={labelStyle}>Type</label>
                <select value={carForm.type} onChange={e => setCarForm({ ...carForm, type: e.target.value })} style={inputStyle}>
                  {['Sedan', 'SUV', 'Truck', 'Hatchback', 'Van'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Transmission</label>
                <select value={carForm.transmission} onChange={e => setCarForm({ ...carForm, transmission: e.target.value })} style={inputStyle}>
                  {['Automatic', 'Manual', 'CVT'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div><label style={labelStyle}>Location</label><input value={carForm.location} onChange={e => setCarForm({ ...carForm, location: e.target.value })} placeholder="e.g. Nairobi" style={inputStyle} /></div>
            <div><label style={labelStyle}>Description</label><textarea value={carForm.description} onChange={e => setCarForm({ ...carForm, description: e.target.value })} placeholder="Describe the car..." rows={4} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            <div><label style={labelStyle}>WhatsApp Contact</label><input value={carForm.whatsapp} onChange={e => setCarForm({ ...carForm, whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} /></div>
          </>}

          <div style={sectionStyle}>
            <p style={{ color: 'var(--gold)', fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase' }}>🔐 Lister Verification</p>
            <div><label style={labelStyle}>Your Role</label>
              <select value={listerForm.role} onChange={e => setListerForm({ ...listerForm, role: e.target.value })} style={inputStyle}>
                {(activeTab === 'land' ? ['Agent', 'Manager', 'Director', 'CEO'] : ['Caretaker', 'Owner', 'Building Manager', 'Agent']).map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Your WhatsApp Number</label>
              <input value={listerForm.whatsapp} onChange={e => setListerForm({ ...listerForm, whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Building Agreement (Optional but Recommended)</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setAgreementFile(e.target.files?.[0] || null)} style={{ color: 'var(--text)', fontSize: '13px' }} />
              <div style={{ background: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.15)', borderRadius: '6px', padding: '12px 16px', marginTop: '12px' }}>
                <p style={{ color: '#f87171', fontSize: '12px', lineHeight: 1.6 }}>
                  ⚠️ If you don't upload the agreement now, please have the <strong>physical document ready</strong> during the site visit, or send it via WhatsApp before the listing goes live.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Photos (Max {activeTab === 'land' ? 10 : 7} — First photo must be front/main photo)</label>
            {activeTab === 'land' && (
              <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '6px', padding: '12px 14px', marginBottom: '10px' }}>
                <p style={{ color: 'var(--gold)', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Recommended photo order for land:</p>
                <p style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                  1. Entrance/access point · 2. Wide boundary shot · 3-6. Each corner/beacon · 7. The access road · 8. Soil/terrain close-up · 9. Surroundings · 10. Nearby landmark
                </p>
              </div>
            )}
            <input type="file" accept="image/*" multiple onChange={handleImageChange} style={{ color: 'var(--text)', fontSize: '13px' }} />
            {previews.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px', marginTop: '16px' }}>
                {previews.map((url, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={url} alt={`preview-${i}`} style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '4px', border: i === 0 ? '2px solid var(--gold)' : '1px solid rgba(201,168,76,0.2)' }} />
                    {i === 0 && <span style={{ position: 'absolute', bottom: '4px', left: '4px', background: 'var(--gold)', color: '#000', fontSize: '9px', padding: '2px 6px', borderRadius: '2px' }}>MAIN</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle}>Video Tour Link (optional, recommended)</label>
            <input value={videoUrl} onChange={e => setVideoUrl(e.target.value)}
              placeholder="Paste a YouTube or Google Drive link" style={inputStyle} />
            <p style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '6px' }}>
              A short walkthrough video builds trust — upload to YouTube (unlisted is fine) or Google Drive, then paste the link here.
            </p>
          </div>

          {error && <p style={{ color: '#f87171', fontSize: '13px' }}>{error}</p>}
          {success && <p style={{ color: '#4ade80', fontSize: '13px' }}>{success}</p>}

          <button onClick={handleSubmit} className="btn-primary" disabled={loading} style={{ padding: '16px', fontSize: '14px' }}>
            {loading ? 'Uploading...' : 'Add Listing'}
          </button>
        </div>
        </>}
      </div>
    </div>
  );
};

export default AdminPage;
